import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import { LayoutManagerComponent } from '@helios/layout-manager'
import { PanelMetadata } from '@helios/panel-base'
import { merge, Subject } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'

@Component({
  selector: 'hs-panel-card',
  templateUrl: './panel-card.component.html',
  styleUrls: ['./panel-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelCardComponent implements OnInit, OnDestroy {
  @Input() panelMetadata?: PanelMetadata
  @Input() layoutManager?: LayoutManagerComponent

  /** @internal */
  isPanelAdded = false

  @ViewChild('dragZone')
  dragAndDrop?: ElementRef<HTMLElement>

  @HostBinding('attr.aria-label')
  get ariaLabel() {
    return `${this.isPanelAdded ? `Focus` : 'Add'} ${
      this.panelMetadata?.title
    } Panel`
  }

  @HostBinding('attr.role')
  get role() {
    return this.isPanelAdded ? 'button' : ''
  }
  @HostBinding('class.activePanel')
  get activePanel() {
    return this.isPanelAdded
  }
  @HostBinding('class.no-access')
  get noAccess() {
    return this.panelMetadata?.hasAccess !== true
  }
  @HostBinding('attr.tabindex')
  get tabIndex() {
    return this.isPanelAdded ? 0 : -1
  }

  get label() {
    return `${this.isPanelAdded ? 'Close' : 'Add'} ${
      this.panelMetadata?.title
    } Panel`
  }

  private readonly destroySubject$ = new Subject<void>()

  constructor(private readonly changeDetector: ChangeDetectorRef) {}

  @HostListener('click', ['$event'])
  @HostListener('keydown.enter', ['$event'])
  focusPanel(e?: MouseEvent | KeyboardEvent): void {
    // Don't allow interaction if user doesn't have access
    if (this.panelMetadata?.hasAccess !== true) return
    // Don't focus when any buttons in the card have been pressed.
    if (this.isPanelAdded && (e?.target as HTMLElement)?.nodeName !== 'BUTTON')
      this.layoutManager?.focusPanel(this.panelMetadata?.key)
  }

  ngOnInit() {
    if (!this.panelMetadata || !this.layoutManager) return
    this.isPanelAdded = this.layoutManager?.isPanelAdded(
      this.panelMetadata?.key,
    )

    merge(this.layoutManager?.panelAdded$, this.layoutManager?.panelRemoved$)
      .pipe(
        takeUntil(this.destroySubject$),
        filter(key => key == this.panelMetadata?.key),
      )
      .subscribe(() => {
        this.isPanelAdded = !!this.layoutManager?.isPanelAdded(
          this.panelMetadata?.key,
        )
        this.changeDetector.markForCheck()
      })
  }

  ngOnDestroy() {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  togglePanel(event: Event): void {
    event.stopPropagation()
    if (!this.panelMetadata) return
    // Prevent toggling if user doesn't have access
    if (this.panelMetadata.hasAccess !== true) return

    if (this.isPanelAdded) {
      this.layoutManager?.removePanel(this.panelMetadata)
    } else {
      this.layoutManager?.addPanel(this.panelMetadata)
    }
  }
}
